<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';
import { countBy } from 'lodash-es';
import {
  computed,
  nextTick,
  ref,
  toRef,
  watch,
} from 'vue';

import type { DateSingleOrMultipleInputType } from '@modules/forms/types';
import useDateInput from '@modules/forms/components/inputs/composables/useDateInput';
import VFormFloatLabelInputWrapper
  from '@modules/forms/components/inputs/VFormFloatLabelInputWrapper/VFormFloatLabelInputWrapper.vue';
import VFormInputWrapper
  from '@modules/forms/components/inputs/VFormInputWrapper/VFormInputWrapper.vue';
import { FORM_DATE_INPUT_PT_BORDER_FIX } from '@modules/forms/constants';
import { isArray, isDate } from '@utils/guards';

import type {
  VFormDateInputEmits,
  VFormDateInputProps,
} from './types';
import { useInputInnerValue } from '../composables';

const props = withDefaults(defineProps<VFormDateInputProps>(), {});
const emit = defineEmits<VFormDateInputEmits>();

interface DatePickerLike extends ComponentPublicInstance {
  rawValue?: DateSingleOrMultipleInputType;
  updateCurrentMetaData?(): void;
}

const { innerValue, setValue } = useInputInnerValue<DateSingleOrMultipleInputType>(toRef(props, 'value'));
const {
  format,
  isMobile,
  inputPtClass,
  adjustDateForViewMode,
  monthPtFunction,
  yearPtFunction,
} = useDateInput(toRef(props, 'dateFormat'));

const calendarRef = ref<DatePickerLike | null>(null);

function syncCalendarView(value?: DateSingleOrMultipleInputType): void {
  if (!calendarRef.value)
    return;

  // Work around PrimeVue calendar UI desync when v-model is changed programmatically.
  calendarRef.value.rawValue = value;
  calendarRef.value.updateCurrentMetaData?.();
}

function getValue(value?: DateSingleOrMultipleInputType): DateSingleOrMultipleInputType {
  if (isArray(value)) {
    const dates = value.map((val) => isDate(val) ? adjustDateForViewMode(props.view, val) : val);
    const grouped = countBy(dates.map((date) => date.toISOString()));
    return dates.filter((date) => grouped[date.toISOString()] === 1).sort((a, b) => a.getTime() - b.getTime());
  } else {
    return isDate(value) ? adjustDateForViewMode(props.view, value) : value;
  }
}

const adjustedValue = computed<DateSingleOrMultipleInputType>({
  get: () => innerValue.value,
  set: (value) => {
    const newValue = getValue(value);
    setValue(newValue);
    return newValue;
  },
});

watch(format, async () => {
  const val = innerValue.value;
  setValue(undefined);
  await nextTick();
  setValue(val);
});

watch(() => props.value, async (value) => {
  await nextTick();
  // Keep the visible calendar view aligned with externally updated model value.
  syncCalendarView(value);
});
</script>

<template>
  <VFormInputWrapper v-data-qa-auto-id="'VFormDateInput'"
    :errors="errors"
    :class="isFullWidth ? 'w-full' : 'input-max-width'"
    :hide-errors="hideErrors"
  >
    <VFormFloatLabelInputWrapper
      :name="name"
      :label="label"
      :required="required"
      :readonly="readonly"
    >
      <PInputCalendar
        ref="calendarRef"
        v-model="adjustedValue"
        :name="name"
        :input-id="name"
        :placeholder="placeholder"
        :required="required"
        :readonly="readonly"
        :disabled="disabled"
        :date-format="format"
        :touch-u-i="isMobile"
        :manual-input="false"
        :selection-mode="selectionMode"
        :view="view"
        :show-time="showTime"
        :min-date="minDate"
        :max-date="maxDate"
        show-icon
        icon-display="input"
        hide-on-date-time-select
        hide-on-range-selection
        class="w-full"
        :class="{ 'p-invalid': isInvalid && !readonly }"
        :pt="{
          pcInputText: {
            root: {
              class: {
                [inputPtClass]: true,
                'input-readonly': readonly,
              },
              autocomplete: autocomplete ? autocomplete : 'off',
            },
          },
          month: monthPtFunction,
          year: yearPtFunction,
          ...FORM_DATE_INPUT_PT_BORDER_FIX,
        }"
        @close="emit('blur', adjustedValue)"
        @focus="emit('focus', adjustedValue)"
        @update:model-value="emit('change', adjustedValue)"
        @input="emit('input', adjustedValue)"
      />
    </VFormFloatLabelInputWrapper>
  </VFormInputWrapper>
</template>
